import { isDefined } from "@homebound/activesupport";
import {
  collapseColumn,
  column,
  Css,
  emptyCell,
  GridColumn,
  GridDataRow,
  GridTable,
  numericColumn,
  simpleHeader,
} from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { useCallback, useMemo } from "react";
import { DisplayNamedFragment } from "src/fragments/commonFragments.generated";
import { CostType } from "src/generated/graphqlTypes";
import { TruncatedNameListCell } from "src/routes/libraries/plan-package/takeoffs/components/TakeoffsTable/TruncatedNameListCell";
import { createProductOfferingScopeUrl } from "src/RouteUrls";
import { maybeAddIdColumn } from "src/utils/idColumn";
import { openNewTab } from "src/utils/window";
import { scopeDiffStyles } from "../../components/utils";
import { PlanBasedBidsTableHeader } from "./PlanBasedBidsTableHeader";
import { EditFormValues } from "./PlanBasedBidsTableV2";
import {
  PlanBasedBidsTableV2_BidPackageFragment,
  PlanBasedBidsTableV2_BidPackageScopeLineFragment,
  PlanBasedBidsTableV2_BidPlanChildGroupLineFragment,
  PlanBasedBidsTableV2_BidPlanChildGroupScopedLineFragment,
  PlanBasedBidsTableV2_BidPlanGroupFragment,
  PlanBasedBidsTableV2_NarrowedBidContractRevisionFragment,
  PlanBasedBidsTableV2_TakeoffLineItemVersionFragment,
} from "./PlanBasedBidsTableV2.generated";
import {
  tradeColumnDescriptors,
  tradeColumnHeader,
  tradeCostCell,
  TradeCostColumnsData,
} from "./planBasedTradeColumns";

type PlanBasedScopeTableProps = {
  bidPackage: PlanBasedBidsTableV2_BidPackageFragment;
  // The bid plan groups this table renders
  bidPlanGroups: PlanBasedBidsTableV2_BidPlanGroupFragment[];
  // The scope buckets for this table: this child group's matched line buckets (groupLine != null), or
  // the single catch-all bucket (groupLine == null) when rendering the BPG's catch-all table.
  scopedLineItems: PlanBasedBidsTableV2_BidPlanChildGroupScopedLineFragment[];
  mode: "view" | "edit";
  formState: ObjectState<EditFormValues> | undefined;
  bidContractRevision: PlanBasedBidsTableV2_NarrowedBidContractRevisionFragment | undefined;
  searchFilter?: string;
};

export function PlanBasedScopeTable(props: PlanBasedScopeTableProps) {
  const { bidPackage, bidPlanGroups, scopedLineItems, mode, formState, bidContractRevision, searchFilter } = props;
  return (
    <>
      {(scopedLineItems ?? [])
        .groupByObject((sli) => sli.childGroup)
        .map(([childGroup, lines]) => (
          <div css={Css.mb2.$} key={childGroup.id}>
            <PlanBasedScopeTableView
              bidPackage={bidPackage}
              bidPlanGroup={bidPlanGroups.find((bpg) => bpg.childGroups.some((cg) => cg.id === childGroup.id))!}
              childGroup={childGroup}
              scopedLineItems={lines}
              mode={mode}
              formState={mode === "edit" ? formState : undefined}
              bidContractRevision={bidContractRevision}
              searchFilter={searchFilter}
            />
          </div>
        ))}
    </>
  );
}

type PlanBasedScopeTableViewProps = Pick<
  PlanBasedScopeTableProps,
  "mode" | "formState" | "bidContractRevision" | "searchFilter" | "scopedLineItems"
> & {
  bidPackage: PlanBasedBidsTableV2_BidPackageFragment;
  // The location-specific child group this table renders (one per location for multi-location add-ons).
  // Drives the header title (the child group's name, e.g. "Add Crown Molding - Level 1").
  childGroup: DisplayNamedFragment;
  bidPlanGroup: PlanBasedBidsTableV2_BidPlanGroupFragment;
};

// The Show-Scope view for a single child group (one per location for multi-location add-ons): the child
// group header, then one parent row per matched bid line (carrying the bidder cost columns) over its
// takeoff line items, and finally this child group's "Scope also covered by {child group}" catch-all
// row (its `groupLine == null` bucket) when it has any owned-but-unmatched scope.
function PlanBasedScopeTableView(props: PlanBasedScopeTableViewProps) {
  const { bidPackage, bidPlanGroup, childGroup, scopedLineItems, mode, formState, bidContractRevision, searchFilter } =
    props;
  const activeBidders = bidPackage.activeBidders;

  // When narrowed to a specific BCR, only render that BCR's bidder; otherwise render all active bidders.
  const bidders = useMemo(
    () =>
      bidContractRevision
        ? activeBidders.filter((b) => b.bidContract?.id === bidContractRevision.bidContract.id)
        : activeBidders,
    [activeBidders, bidContractRevision],
  );
  const bcrs = useMemo(
    () => (bidContractRevision ? [bidContractRevision] : bidPackage.latestVersion.latestBidContractRevisions),
    [bidContractRevision, bidPackage],
  );

  const costRowLookup = useCallback(
    (bpglId: string, costCodeId: string) =>
      formState?.costs.rows.find((r) => r.value.groupLineId === bpglId && r.value.costCodeId === costCodeId),
    [formState],
  );

  const tradeCostContext: TradeCostColumnsData = useMemo(
    () => ({
      activeBidders: bidders,
      bidContractRevisions: bcrs,
      costCodes: bidPackage.costCodes,
      mode,
      costRowLookup,
    }),
    [bidders, bcrs, bidPackage.costCodes, mode, costRowLookup],
  );

  const columns = useMemo(
    () => createColumns(tradeCostContext, bidders.nonEmpty, bidPlanGroup.isNoCost),
    [tradeCostContext, bidders, bidPlanGroup],
  );
  const rows = useMemo(() => createRows(childGroup.displayName, scopedLineItems), [childGroup, scopedLineItems]);

  return (
    <div>
      <PlanBasedBidsTableHeader bidPlanGroup={bidPlanGroup} displayName={childGroup.displayName} />
      <GridTable columns={columns} filter={searchFilter} rows={rows} id={`scope-${childGroup.id}`} />
    </div>
  );
}

// A matched bid line, or the catch-all bucket, rendered as a parent row over its takeoff line items. Matched
// rows carry `line` so the bidder cost columns render; the catch-all has no line (and no cost).
type GroupRow = { kind: "group"; data: { name: string; line?: PlanBasedBidsTableV2_BidPlanChildGroupLineFragment } };
// The TLI with its version detail (current+previous+changeType)
type TliRow = { kind: "scope"; data: PlanBasedBidsTableV2_BidPackageScopeLineFragment };
type HeaderRow = { kind: "header"; data: undefined };
type Row = HeaderRow | GroupRow | TliRow;

function createColumns(ctx: TradeCostColumnsData, hasTrades: boolean, isNoCost: boolean): GridColumn<Row>[] {
  const scopeColumns: GridColumn<Row>[] = [
    collapseColumn<Row>({ scope: (scope) => ({ content: emptyCell.content, css: scopeDiffStyles(scope) }) }),
    ...maybeAddIdColumn(
      column<Row>({
        header: "ID",
        group: emptyCell,
        scope: (scope) => ({ content: scope.takeoffLineItemVersion.id, css: scopeDiffStyles(scope) }),
        mw: "100px",
      }),
    ),
    column<Row>({
      header: "Item Code",
      // The parent (bid line / catch-all) labels the first column
      group: ({ name }) => ({ content: name, css: Css.smSb.gray900.truncate.$ }),
      scope: (scope) => ({
        content: `${scope.takeoffLineItemVersion.item.fullCode} ${scope.takeoffLineItemVersion.item.name}`,
        onClick: () => openTakeoffLineInNewTab(scope.takeoffLineItemVersion),
        css: scopeDiffStyles(scope),
      }),
      mw: "300px",
    }),
    column<Row>({
      header: "Name",
      group: emptyCell,
      scope: (scope) => ({ content: scope.takeoffLineItemVersion.name, css: scopeDiffStyles(scope) }),
      mw: "200px",
    }),
    column<Row>({
      header: "Cost Type",
      group: emptyCell,
      scope: (scope) => ({ content: scope.takeoffLineItemVersion.costTypeDetail.name, css: scopeDiffStyles(scope) }),
      mw: "100px",
    }),
    column<Row>({
      header: "Location",
      group: emptyCell,
      scope: (scope) => ({
        content: scope.takeoffLineItemVersion.location.displayLocationPath,
        css: scopeDiffStyles(scope, "location"),
      }),
      mw: "100px",
    }),
    column<Row>({
      header: "Option",
      group: emptyCell,
      // Show the takeoff line's option names; the cell truncates and a tooltip reveals the full list on hover.
      scope: (scope) => {
        const names = scope.takeoffLineItemVersion.options.sortByKey("order").map((rpo) => rpo.name);
        return { content: <TruncatedNameListCell nameList={names} />, css: scopeDiffStyles(scope, "options") };
      },
      mw: "200px",
    }),
    column<Row>({
      header: "Material/Labor",
      group: emptyCell,
      // Labor lines have no material variant, so just label them "Labor"; everything else shows its material code.
      scope: (scope) => ({
        content:
          scope.takeoffLineItemVersion.costTypeDetail.code === CostType.Labor
            ? "Labor"
            : scope.takeoffLineItemVersion.materialVariant?.code,
        css: scopeDiffStyles(scope, "materialVariant"),
      }),
      mw: "200px",
    }),
    numericColumn<Row>({
      header: "QTY",
      group: emptyCell,
      // "N/A" for units that ignore quantity (e.g. Lump Sum/Allowance), matching the takeoff scope table.
      scope: (scope) => ({
        content: scope.takeoffLineItemVersion.unitOfMeasure.useQuantity
          ? (scope.takeoffLineItemVersion.quantity ?? "")
          : "N/A",
        css: scopeDiffStyles(scope, "quantity"),
      }),
      mw: "50px",
    }),
    column<Row>({
      header: "UoM",
      group: emptyCell,
      scope: (scope) => ({
        content: scope.takeoffLineItemVersion.unitOfMeasure.abbreviation,
        css: scopeDiffStyles(scope, "unitOfMeasure"),
      }),
      mw: "75px",
    }),
  ];

  // The shared bidder × cost-code cost columns, rendered on the matched bid line rows so costs can still be
  // entered/viewed while Show Scope is on. Skip them entirely when there are no bidders yet.
  const tradeColumns = hasTrades
    ? tradeColumnDescriptors(ctx).map((d) =>
        numericColumn<Row>({
          header: tradeColumnHeader(d),
          group: ({ line }) => (line ? tradeCostCell(line, d, ctx, isNoCost) : emptyCell),
          scope: (scope) => ({ content: emptyCell.content, css: scopeDiffStyles(scope) }),
          w: "300px",
        }),
      )
    : [];

  return [...scopeColumns, ...tradeColumns];
}

function createRows(
  childGroupDisplayName: string,
  scopedLines: PlanBasedBidsTableV2_BidPlanChildGroupScopedLineFragment[],
): GridDataRow<Row>[] {
  // Each matched bid line is a parent row (labeled by its options, carrying the bidder cost) over its takeoff
  // line items. The BE already orders these contiguously per child group.
  const lineRows = scopedLines
    .filter((sl) => isDefined(sl.groupLine))
    .map((sl) => ({
      kind: "group" as const,
      id: sl.groupLine!.id,
      data: { name: sl.groupLine!.displayName, line: sl.groupLine! },
      children: sl.scopeLines.map((scopeLine) => ({
        kind: "scope" as const,
        id: `${sl.groupLine!.id}:${scopeLine.takeoffLineItemVersion.id}`,
        data: scopeLine,
      })),
    }));

  // This child group's catch-all bucket (groupLine == null) renders as a parent row at the end.
  const unmatchedOwnedScope = scopedLines.find((sl) => !isDefined(sl.groupLine));
  const catchAllRow =
    unmatchedOwnedScope && unmatchedOwnedScope.scopeLines.nonEmpty
      ? [
          {
            kind: "group" as const,
            id: unmatchedOwnedScope.id, // unmatched:childGroupId
            data: { name: `Scope also covered by ${childGroupDisplayName}` },
            children: unmatchedOwnedScope.scopeLines.map((scope) => ({
              kind: "scope" as const,
              id: `${unmatchedOwnedScope.id}:${scope.takeoffLineItemVersion.id}`,
              data: scope,
            })),
          },
        ]
      : [];

  return [simpleHeader, ...lineRows, ...catchAllRow];
}

// Open the takeoff line item in a new tab, pre-filtered to its cost code and searched by its material/item code.
function openTakeoffLineInNewTab(tliv: PlanBasedBidsTableV2_TakeoffLineItemVersionFragment): void {
  const url = createProductOfferingScopeUrl(tliv.readyPlan.id, tliv.readyPlan.version.id, {
    search: tliv.materialVariant?.code ?? tliv.item.fullCode,
    filter: { costCode: [tliv.item.costCode.id] },
  });
  openNewTab(url);
}
